import debounce from "lodash.debounce"
import { ValidationSchemaKey, validate } from "../validators/validate"


export type FieldValues = { [key: string]: string }
export type FieldBools = { [key: string]: boolean }
export type FieldFnVoid = { [key: string]: () => void }
export type FieldKey = keyof FieldValues
type DebouncedSetShowError = (key: FieldKey) => void


/**
 * Creates a new object where each key from the input object is set to an empty string.
 * 
 * @param FieldBools inputObj - The input object with boolean values
 * @returns FieldValues - The new object with each key set to an empty string
 */
export const createValues = (inputObj: FieldBools): FieldValues => {
  return Object.keys(inputObj).reduce((acc, key) => {
    return { ...acc, [key]: '' }
  }, {})
}

/**
 * Creates a new object where each key from the input object is set to an empty string.
 * 
 * @param FieldBools inputObj - The input object with boolean values
 * @returns FieldValues - The new object with each key set to an empty string
 */
export const createBoolValues = (inputObj: FieldBools): FieldBools => {
  return Object.keys(inputObj).reduce((acc, key) => {
    return { ...acc, [key]: false }
  }, {})
}

/**
 * Creates an object with validation errors for each key in the inputObj.
 * 
 * @param FieldBools inputObj - The input object with boolean values
 * @param FieldValues values - The values to validate
 * @returns FieldValues - The object with validation errors
 */
export const createValidationErrors = (inputObj: FieldBools, values: FieldValues): FieldValues => {
  return Object.keys(inputObj).reduce((acc, key) => {
    (inputObj[key]) && (acc[key] = validate(key as ValidationSchemaKey, values[key] || ''))
    return acc
  }, {} as FieldValues)
}

/**
 * Creates a debounced map for each key in the inputObj.
 * 
 * @param {FieldBools} inputObj - The input object with boolean values
 * @returns {Record<FieldKey, () => void>} The debounced map
 */
export const createDebounceMap = (inputObj: FieldBools, debouncedSetShowError: DebouncedSetShowError): FieldFnVoid => {
  return Object.keys(inputObj).reduce((acc, key) => {
    acc[key as FieldKey] = debounce(() => debouncedSetShowError(key as FieldKey), 1500)
    return acc
  }, {} as Record<FieldKey, () => void>)
}


/**
 * Checks if the required fields are valid in the provided data.
 * 
 * @param {FieldValues} requiredDetails - The application form state answers for the required details fields.
 * @param {FieldBools} [requiredFields] - An optional object specifying which fields are required.
 * @returns {boolean} - Returns true if any of the required fields are valid, otherwise false.
 */
export const checkAreRequiredFieldsValid = (requiredDetails: FieldValues | undefined, requiredFields?: FieldBools): boolean  => {
    if (requiredDetails && requiredFields) {
      const fieldsValidation = Object.keys(requiredFields || {}).map(key => {
        return requiredFields[key] ? requiredDetails[key] : false
      })
      return fieldsValidation.some(field => field)
    }
    return true
}

/**
 * Checks if there are any errors in the provided array of error messages.
 * 
 * @param {string[]} errors - An array of error messages.
 * @returns {boolean} - Returns true if all errors are empty, otherwise false.
 */
export const checkErrs = (errors: string[]): boolean => {
  return !errors.some(value => value.length > 0)
}



